import { useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { ApiProvider, createQueryClient } from '@blimo/api-client/react';
import { Toaster, TooltipProvider, useTheme } from '@blimo/ui';
import { createAppRouter } from './router.jsx';
import { AppBranding } from './lib/branding.jsx';
import { defaultOfflineStore } from './lib/offline-queue.js';
import { MeterQueueProvider } from './lib/meter-queue.jsx';

/**
 * @param {{ api: import('@blimo/api-client').BlimoApi, history?: import('@tanstack/react-router').RouterHistory, offlineStore?: import('./lib/offline-queue.js').OfflineStore }} props
 *   offlineStore: storage of the meter reading offline queue (default IndexedDB; tests inject a memory store)
 */
export function App({ api, history, offlineStore }) {
  const [store] = useState(() => offlineStore ?? defaultOfflineStore());
  const [{ queryClient, router }] = useState(() => {
    /** @type {ReturnType<typeof createAppRouter>} */
    let appRouter;
    const client = createQueryClient({
      onUnauthenticated: () => {
        client.clear();
        const { pathname, href } = appRouter.state.location;
        if (!pathname.startsWith('/login')) appRouter.navigate({ to: '/login', search: { redirect: href } });
      },
    });
    appRouter = createAppRouter({ queryClient: client, api, history });
    return { queryClient: client, router: appRouter };
  });

  return (
    <ApiProvider api={api}>
      <QueryClientProvider client={queryClient}>
        <MeterQueueProvider store={store}>
          <TooltipProvider>
            <AppBranding>
              <RouterProvider router={router} />
            </AppBranding>
            <Toaster position="top-center" />
            <ThemeColorSync />
          </TooltipProvider>
        </MeterQueueProvider>
      </QueryClientProvider>
    </ApiProvider>
  );
}

/** Status bar / browser chrome colour follows the chosen colour scheme (also when it differs from the OS). */
function ThemeColorSync() {
  const { resolvedTheme } = useTheme();
  useEffect(() => {
    const color = resolvedTheme === 'dark' ? '#0a0a0a' : '#ffffff';
    document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => meta.setAttribute('content', color));
  }, [resolvedTheme]);
  return null;
}
